'use client'

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@planner/ui'
import type { Payment } from '@planner/types'

interface PaymentFormProps {
  eventId: string
  payment?: Payment | null
  onClose: () => void
}

export function PaymentForm({ eventId, payment, onClose }: PaymentFormProps) {
  const [formData, setFormData] = useState({
    amount: '',
    due_date: '',
    payment_date: '',
    status: 'oczekuje' as Payment['status'],
    invoice_link: '',
    notes: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount.toString(),
        due_date: payment.due_date ? new Date(payment.due_date).toISOString().slice(0, 16) : '',
        payment_date: payment.payment_date ? new Date(payment.payment_date).toISOString().slice(0, 16) : '',
        status: payment.status,
        invoice_link: payment.invoice_link || '',
        notes: payment.notes || '',
      })
    } else {
      // Set default due date to 7 days from now
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 7)
      setFormData({
        ...formData,
        due_date: defaultDate.toISOString().slice(0, 16),
      })
    }
  }, [payment])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const paymentData = {
        event_id: eventId,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : null,
        status: formData.status,
        invoice_link: formData.invoice_link || null,
        notes: formData.notes || null,
      }

      if (payment) {
        const { error: updateError } = await supabase
          .from('payments')
          .update(paymentData)
          .eq('id', payment.id)

        if (updateError) throw updateError
      } else {
        const { error: insertError } = await supabase
          .from('payments')
          .insert([paymentData])

        if (insertError) throw insertError
      }

      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {payment ? 'Edytuj płatność' : 'Dodaj płatność'}
        </CardTitle>
        <CardDescription>
          {payment ? 'Edytuj szczegóły płatności' : 'Dodaj nową płatność do wydarzenia'}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="amount" className="text-sm font-medium">
                  Kwota (zł) *
                </label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="status" className="text-sm font-medium">
                  Status
                </label>
                <select
                  id="status"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Payment['status'] })}
                >
                  <option value="oczekuje">Oczekuje</option>
                  <option value="oplacone">Opłacone</option>
                  <option value="przeterminowane">Przeterminowane</option>
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="due_date" className="text-sm font-medium">
                  Termin płatności *
                </label>
                <Input
                  id="due_date"
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="payment_date" className="text-sm font-medium">
                  Data płatności
                </label>
                <Input
                  id="payment_date"
                  type="datetime-local"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="invoice_link" className="text-sm font-medium">
                  Link do faktury
                </label>
                <Input
                  id="invoice_link"
                  type="url"
                  value={formData.invoice_link}
                  onChange={(e) => setFormData({ ...formData, invoice_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="notes" className="text-sm font-medium">
                  Notatki
                </label>
                <textarea
                  id="notes"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            {error && (
              <div className="text-sm text-destructive">{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Anuluj
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            </div>
          </form>
        </CardContent>
    </Card>
  )
}

